import { useEffect, useState } from "react";
import { Loader2, FileText, Download, ShieldCheck, Calendar, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Layout } from "../components/Layout";

const AUDIT_TYPES = [
  { id: "medicaid",  label: "Medicaid Survey",          desc: "For state Medicaid surveyor visits" },
  { id: "hipaa",     label: "HIPAA Compliance Review",  desc: "For HIPAA security reviews" },
  { id: "insurance", label: "Insurance Audit",          desc: "For private insurance audits" },
  { id: "internal",  label: "Internal Compliance",      desc: "For internal QA and recordkeeping" },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function isoMinusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AuditPacketPage() {
  const [form, setForm] = useState({
    start_date: isoMinusDays(30),
    end_date: todayISO(),
    audit_type: "medicaid",
  });
  const [submitting, setSubmitting] = useState(false);
  const [packets, setPackets] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  async function refresh() {
    setLoadingList(true);
    try {
      const r = await api.get("/audit-packet/list");
      setPackets(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function generate(e) {
    e.preventDefault();
    if (form.end_date < form.start_date) {
      toast.error("End date must be on or after start date.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/audit-packet/generate", form);
      toast.success("Audit Preparation Packet generated.");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  async function downloadPacket(p) {
    try {
      const res = await api.get(`/audit-packet/download/${p.id}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = p.file_name || `audit-packet-${p.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  return (
    <Layout>
      <div data-testid="audit-packet-page" className="space-y-10">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Compliance</p>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-[#1C211F]">
            Generate Audit Preparation Packet
          </h1>
          <p className="mt-3 text-sm text-[#57605A] max-w-2xl">
            One-click PDF export of audit logs, credentials, and employee compliance records for any
            date range. Tamper-evident with a SHA-256 fingerprint. Hand to a Medicaid surveyor,
            insurance auditor, or internal compliance reviewer.
          </p>
        </div>

        {/* Generate form */}
        <form onSubmit={generate} className="bg-white border border-[#E8E6E1] rounded-xl p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Start date</span>
              <input
                data-testid="ap-start-date"
                type="date" required
                value={form.start_date}
                max={form.end_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="mt-2 w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">End date</span>
              <input
                data-testid="ap-end-date"
                type="date" required
                value={form.end_date}
                min={form.start_date}
                max={todayISO()}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="mt-2 w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
              />
            </label>
          </div>

          <div>
            <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Audit type</span>
            <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {AUDIT_TYPES.map((t) => {
                const selected = form.audit_type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`ap-type-${t.id}`}
                    onClick={() => setForm({ ...form, audit_type: t.id })}
                    className={`text-left rounded-md border p-3 transition-colors duration-200 ${
                      selected
                        ? "border-[#2E5C50] bg-[#D6E0D9]/40"
                        : "border-[#E8E6E1] bg-white hover:border-[#2E5C50]/40"
                    }`}
                  >
                    <p className={`text-sm font-semibold ${selected ? "text-[#2E5C50]" : "text-[#1C211F]"}`}>{t.label}</p>
                    <p className="mt-0.5 text-xs text-[#57605A]">{t.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              data-testid="ap-generate-btn"
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
              {submitting ? "Generating…" : "Generate Audit Preparation Packet"}
            </button>
            <span className="inline-flex items-center gap-1.5 text-xs text-[#57605A]">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> SHA-256 tamper-evidence applied.
            </span>
          </div>
        </form>

        {/* History */}
        <div>
          <h2 className="font-heading text-lg text-[#1C211F]">Generated packets</h2>
          <p className="mt-1 text-sm text-[#57605A]">All packets you've generated. Click <em>Download</em> for the PDF.</p>

          <div className="mt-4 bg-white border border-[#E8E6E1] rounded-xl overflow-hidden">
            {loadingList ? (
              <div className="flex items-center justify-center py-14">
                <Loader2 className="w-5 h-5 text-[#2E5C50] animate-spin" />
              </div>
            ) : packets.length === 0 ? (
              <div data-testid="ap-empty-state" className="flex items-center justify-center gap-2 py-14 text-sm text-[#57605A]">
                <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                No audit preparation packets yet. Generate your first one above.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F9F7F4] text-[#57605A] text-xs uppercase tracking-[0.12em]">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold">Generated</th>
                    <th className="text-left py-3 px-4 font-semibold">Type</th>
                    <th className="text-left py-3 px-4 font-semibold">Range</th>
                    <th className="text-left py-3 px-4 font-semibold">Contents</th>
                    <th className="text-left py-3 px-4 font-semibold">Size</th>
                    <th className="text-right py-3 px-4 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {packets.map((p) => (
                    <tr key={p.id} data-testid={`ap-row-${p.id}`} className="border-t border-[#E8E6E1] hover:bg-[#F9F7F4]/50 transition-colors">
                      <td className="py-3 px-4 text-[#1C211F]">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#2E5C50]" strokeWidth={1.5} />
                          {fmtDate(p.created_at)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-[#1C211F]">{p.audit_type_label}</td>
                      <td className="py-3 px-4 text-[#57605A]">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {p.start_date} → {p.end_date}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-[#57605A]">
                        {p.counts?.audit_logs || 0} logs · {p.counts?.visits || 0} visits · {p.counts?.caregivers || 0} caregivers · {p.counts?.documents || 0} docs
                      </td>
                      <td className="py-3 px-4 text-[#57605A]">{fmtBytes(p.file_size_bytes)}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          data-testid={`ap-download-${p.id}`}
                          onClick={() => downloadPacket(p)}
                          className="inline-flex items-center gap-1.5 text-[#2E5C50] hover:text-[#22463D] text-sm font-medium"
                        >
                          <Download className="w-4 h-4" strokeWidth={1.5} /> Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
